import Chart from "../../../components/chart/chart";
import SubjectProgressCard from "./card";
import AdherenceTable from "./adherence-table";
import ApiSubjectsDashboard from "../../../api/subjects-dashboard";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PageContentLoader from "../../../components/loader/page-content-loader";
import moment from "moment";
import debounce from "../../../common/utils/debounce";

const defaultStartDate = moment().subtract(30, "days").utc().toISOString();
const defaultEndDate = moment().utc().toISOString();

const SubjectDashboard = ({ filterOptions = {} }) => {
  const [pointsByDates, setPointsByDates] = useState({});
  const [totalAdherences, setTotalAdherences] = useState({});
  const [totalPointsSum, setTotalPointsSum] = useState(0);
  const [totalSubjects, setTotalSubjects] = useState(0);
  const [subjectsPerDay, setSubjectsPerDay] = useState([]);
  const [totalAdherenceSum, setTotalAdherenceSum] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const chartContainer = useRef();
  const [chartWidth, setChartWidth] = useState(0);
  const [tab, setTab] = useState(3);
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [isTableLoading, setTableLoading] = useState(false);
  const [sorter, setSorter] = useState({ field: null, order: null });

  const debouncedFetchSubjects = useRef(
    debounce((options) => {
      setLoading(true);

      const finalOptions = {
        ...options,
        periodEnd: options.period?.[options.period.length - 1]?.end || defaultEndDate,
        periodStart: options.period?.[options.period.length - 1]?.start || defaultStartDate,
      };

      ApiSubjectsDashboard.getScores(finalOptions)
        .then((r) => {
          setTotalSubjects(r.data.totalSubjectsCount);
          setSubjectsPerDay(r.data.subjectList);
          setPointsByDates(r.data.totalScores);
          setTotalPointsSum(r.data.totalScoresSum);
          setTotalAdherenceSum(r.data.totalAdherenceSum * 100);
          setTotalAdherences(r.data.totalAdherences);
          setTasks(r.data.totalAdherenceDailyTasks);
          setTimeout(() => setLoading(false), 500);
          setTableLoading(true);
          return ApiSubjectsDashboard.getAdherences(finalOptions);
        })
        .then((r2) => {
          setSubjects(Array.isArray(r2.data.totalScoreBySubjects) ? r2.data.totalScoreBySubjects : []);
        })
        .finally(() => {
          setTimeout(() => setTableLoading(false), 500);
        });
    }, 400)
  ).current;

  const debouncedFetchSubjectsSorter = useRef(
    debounce((options) => {
      setTableLoading(true);
      ApiSubjectsDashboard.getAdherences({
        ...options,
        periodEnd:
          options.period?.[options.period.length - 1]?.end ||
          defaultEndDate,
        periodStart:
          options.period?.[options.period.length - 1]?.start ||
          defaultStartDate,
      }).then((r) => {
        setSubjects(r.data.totalScoreBySubjects);
      }).finally(() => {
        setTimeout(() => setTableLoading(false), 500);
      });
    }, 400)
  ).current;

  const handleSorterChange = (sort) => {
    const options = {
      ...filterOptions,
      sortField: sort.sortField,
      sortOrder: sort.sortOrder,
    };
    setSorter({ field: sort.sortField, order: sort.sortOrder });
    debouncedFetchSubjectsSorter(options);
  };

  useEffect(() => {
    debouncedFetchSubjects({ ...filterOptions, sortField: sorter.field, sortOrder: sorter.order });
  }, [filterOptions]);


  // useEffect(() => {
  //   setChartWidth(chartContainer.current?.offsetWidth - 20);
  // }, [chartContainer.current]);

  useEffect(() => {
    if (!chartContainer.current) return;

    const update = () => {
      const w = Math.max(0, (chartContainer.current?.offsetWidth || 0) - 20);
      setChartWidth(w);
    };

    update();

    const ro = new ResizeObserver(() => {
      update();
    });
    ro.observe(chartContainer.current);

    return () => ro.disconnect();
  }, []);

  return (
    <div style={{ position: "relative" }}>
      {isLoading && <PageContentLoader />}
      <div className="dashboard-item-container">
        <div className="dashboard-summary-cards">
          <div
            onClick={() => setTab(3)}
            className={tab === 3 && "dashboard-summary-cards__border-bottom"}
          >
            <p style={{ fontSize: 32, marginTop: 8 }}>{totalSubjects}</p>
            <p style={{ fontSize: 13 }}>{t("subject_management.active_subjects")}</p>
          </div>

          <div
            onClick={() => setTab(2)}
            className={tab === 2 && "dashboard-summary-cards__border-bottom"}
          >
            <p style={{ fontSize: 32, marginTop: 8 }}>
              {Math.floor(totalAdherenceSum)}%
            </p>
            <p style={{ fontSize: 13 }}>{t("subject_management.adherence")}</p>
          </div>

          <div
            onClick={() => setTab(1)}
            className={tab === 1 && "dashboard-summary-cards__border-bottom"}
          >
            <p style={{ fontSize: 32, marginTop: 8 }}>{Math.round(totalPointsSum * 10) / 10}</p>
            <p style={{ fontSize: 13 }}>
              {t("subject_management.daily_points")}
            </p>
          </div>
        </div>

        <div style={{ padding: 20 }} ref={chartContainer}>
          <Chart
            pointsByDates={
              tab === 1
                ? pointsByDates
                : tab === 2
                ? totalAdherences
                : subjectsPerDay
            }
            dataKey={
              tab === 1
                ? "points"
                : tab === 2
                ? "adherence"
                : "subjects"
            }
            width={chartWidth}
          />
        </div>
      </div>
      <SubjectProgressCard tasks={tasks} />
      <AdherenceTable
        subjects={subjects}
        fetchSubjects={handleSorterChange}
        isLoading={isTableLoading}
      />
    </div>
  );
};

export default SubjectDashboard;
